(() => {
const SECTION_SELECTOR =
  "[data-peguu-material-detail]";

const STORAGE_KEY =
  "peguu:selected-material";

const PURCHASE_DATA_KEYS = [
  "peguu:qr-config",
  "peguu:sticker-order",
  "peguu:sticker-design"
];

const YOUTUBE_API_SRC =
  "https://www.youtube.com/iframe_api";

  /* ==================================================
     UTILIDADES
  ================================================== */

  const createSlug = (value) => {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  const escapeHtml = (value) => {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  };

  const getRequestedSlug = () => {
    const params =
      new URLSearchParams(
        window.location.search
      );

    return createSlug(
      params.get("material")
    );
  };


  /* ==================================================
     SESSION STORAGE
  ================================================== */

  const getStoredMaterial = () => {
    try {
      const stored =
        sessionStorage.getItem(
          STORAGE_KEY
        );

      if (!stored) {
        return null;
      }

      return JSON.parse(stored);
    } catch (error) {
      console.warn(
        "No fue posible recuperar el material guardado:",
        error
      );

      return null;
    }
  };

  const saveStoredMaterial = (
    material
  ) => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(material)
      );
    } catch (error) {
      console.warn(
        "No fue posible guardar temporalmente el material:",
        error
      );
    }
  };

  /* ==================================================
   LIMPIAR DATOS DE COMPRA ANTERIOR
================================================== */

const clearPreviousPurchaseData =
  () => {

    PURCHASE_DATA_KEYS.forEach(
      (key) => {

        try {

          sessionStorage.removeItem(
            key
          );

        } catch (error) {

          console.warn(
            `No fue posible limpiar ${key}:`,
            error
          );
        }

      }
    );
  };

  /* ==================================================
     API
  ================================================== */

  const DETAIL_TIMEOUT_MS = 12000;
  const DETAIL_MAX_ATTEMPTS = 3;
  const DETAIL_RETRY_DELAYS_MS = [0, 500, 1000];
  const inFlightDetailRequests = new Map();

  class MaterialDetailRequestError extends Error {
    constructor(message, details = {}) {
      super(message);
      this.name = "MaterialDetailRequestError";
      Object.assign(this, details);
    }
  }

  const sleep = (delay) => {
    if (!delay) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      window.setTimeout(resolve, delay);
    });
  };

  const fetchMaterialDetailOnce = async (url) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      DETAIL_TIMEOUT_MS
    );

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Accept: "application/json"
        },
        signal: controller.signal
      });

      if (!response.ok) {
        const isServerError = response.status >= 500;

        throw new MaterialDetailRequestError(
          `La API respondió con el estado ${response.status}`,
          {
            kind: isServerError ? "http-5xx" : "http-4xx",
            status: response.status,
            retryable: isServerError
          }
        );
      }

      const payload = await response.json();

      if (!payload?.success || !payload?.data) {
        throw new MaterialDetailRequestError(
          "La API no devolvió información del material.",
          {
            kind: "invalid-response",
            retryable: false
          }
        );
      }

      return payload.data;
    } catch (error) {
      if (error instanceof MaterialDetailRequestError) {
        throw error;
      }

      if (error?.name === "AbortError") {
        throw new MaterialDetailRequestError(
          `La solicitud del detalle superó el tiempo límite de ${DETAIL_TIMEOUT_MS / 1000} segundos.`,
          {
            kind: "timeout",
            retryable: true
          }
        );
      }

      throw new MaterialDetailRequestError(
        "No fue posible conectar con la API del detalle.",
        {
          kind: "network",
          retryable: true,
          cause: error
        }
      );
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const fetchMaterialDetailWithRetries = async (url) => {
    let lastError = null;
    let attempts = 0;

    for (let attempt = 0; attempt < DETAIL_MAX_ATTEMPTS; attempt += 1) {
      await sleep(DETAIL_RETRY_DELAYS_MS[attempt]);
      attempts = attempt + 1;

      try {
        return await fetchMaterialDetailOnce(url);
      } catch (error) {
        lastError = error;

        if (!error.retryable || attempt === DETAIL_MAX_ATTEMPTS - 1) {
          break;
        }
      }
    }

    throw new MaterialDetailRequestError(
      lastError?.message || "No fue posible cargar el detalle.",
      {
        kind: lastError?.kind || "unknown",
        status: lastError?.status,
        attempts,
        retryable: false,
        cause: lastError
      }
    );
  };

  const fetchMaterialDetail =
    (
      detailApiBaseUrl,
      materialId
    ) => {
      const baseUrl =
        String(
          detailApiBaseUrl ||
            ""
        ).replace(/\/+$/, "");

      const url =
        `${baseUrl}/${encodeURIComponent(
          materialId
        )}/detail`;

      const requestKey = String(materialId);
      const existingRequest = inFlightDetailRequests.get(requestKey);

      if (existingRequest) {
        return existingRequest;
      }

      const request = fetchMaterialDetailWithRetries(url).finally(() => {
        if (inFlightDetailRequests.get(requestKey) === request) {
          inFlightDetailRequests.delete(requestKey);
        }
      });

      inFlightDetailRequests.set(requestKey, request);

      return request;
    };

  const findMaterial = (
    materials,
    requestedSlug
  ) => {
    return materials.find(
      (material) => {
        return (
          material?.active ===
            true &&
          createSlug(
            material.name
          ) === requestedSlug
        );
      }
    );
  };

  const isSamplePack = (material) =>
    String(material?.productCode || "")
      .trim()
      .toLowerCase() === "sample_pack";

  const redirectSamplePack = () => {
    window.location.assign("/pages/materiales");
  };


  /* ==================================================
     YOUTUBE
  ================================================== */

  const getYouTubeVideoId = (
    url
  ) => {
    if (!url) {
      return "";
    }

    try {
      const parsedUrl =
        new URL(url);

      if (
        parsedUrl.hostname.includes(
          "youtu.be"
        )
      ) {
        return parsedUrl.pathname
          .replace("/", "")
          .split("/")[0];
      }

      if (
        parsedUrl.pathname.startsWith(
          "/shorts/"
        )
      ) {
        return parsedUrl.pathname
          .replace(
            "/shorts/",
            ""
          )
          .split("/")[0];
      }

      if (
        parsedUrl.pathname.startsWith(
          "/embed/"
        )
      ) {
        return parsedUrl.pathname
          .replace(
            "/embed/",
            ""
          )
          .split("/")[0];
      }

      return (
        parsedUrl.searchParams.get(
          "v"
        ) || ""
      );
    } catch (error) {
      console.warn(
        "No fue posible interpretar la URL de YouTube:",
        url
      );

      return "";
    }
  };

  const getYouTubeThumbnail = (
    url
  ) => {
    const videoId =
      getYouTubeVideoId(url);

    if (!videoId) {
      return "";
    }

    return (
      `https://img.youtube.com/vi/` +
      `${videoId}/hqdefault.jpg`
    );
  };


  /* ==================================================
     SVG PLAY PRINCIPAL
  ================================================== */

  const getMainPlaySvg = () => {
    return `
      <svg
        width="63"
        height="63"
        viewBox="0 0 63 63"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <g
          clip-path="url(#peguu-play-main-clip)"
        >
          <path
            d="M49.4185 0.571095L10.1829 2.77146C4.55903 3.08685 0.255674 7.90156 0.571065 13.5254L2.77143 52.761C3.08682 58.3849 7.90153 62.6882 13.5254 62.3729L52.761 60.1725C58.3849 59.8571 62.6882 55.0424 62.3728 49.4185L60.1725 10.1829C59.8571 4.55906 55.0424 0.255705 49.4185 0.571095Z"
            fill="#835EC7"
          />

          <path
            d="M50.3284 30.4172L17.0103 13.5911L19.1002 50.8576L50.3284 30.4172Z"
            fill="#E6DFF4"
          />
        </g>

        <defs>
          <clipPath
            id="peguu-play-main-clip"
          >
            <rect
              width="59.695"
              height="59.695"
              fill="white"
              transform="translate(0 3.34253) rotate(-3.20984)"
            />
          </clipPath>
        </defs>
      </svg>
    `;
  };


  /* ==================================================
     SVG PLAY MINIATURA
  ================================================== */

  const getThumbnailPlaySvg = (
    index
  ) => {
    return `
      <svg
        width="11"
        height="11"
        viewBox="0 0 11 11"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <g
          clip-path="url(#peguu-play-thumb-${index})"
        >
          <path
            d="M8.42388 0.0973444L1.73577 0.472419C0.777133 0.526181 0.0435823 1.3469 0.0973437 2.30554L0.472419 8.99365C0.52618 9.95229 1.3469 10.6858 2.30554 10.6321L8.99365 10.257C9.95229 10.2032 10.6858 9.38253 10.6321 8.42388L10.257 1.73577C10.2032 0.777133 9.38252 0.0435829 8.42388 0.0973444Z"
            fill="#835EC7"
          />

          <path
            d="M8.57882 5.18495L2.89941 2.31677L3.25567 8.66922L8.57882 5.18495Z"
            fill="#E6DFF4"
          />
        </g>

        <defs>
          <clipPath
            id="peguu-play-thumb-${index}"
          >
            <rect
              width="10.1756"
              height="10.1756"
              fill="white"
              transform="translate(0 0.569763) rotate(-3.20984)"
            />
          </clipPath>
        </defs>
      </svg>
    `;
  };


  /* ==================================================
     CARGA API YOUTUBE
  ================================================== */

  let youtubeApiPromise =
    null;

  const loadYouTubeApi = () => {
    if (
      window.YT &&
      window.YT.Player
    ) {
      return Promise.resolve();
    }

    if (youtubeApiPromise) {
      return youtubeApiPromise;
    }

    youtubeApiPromise =
      new Promise(
        (resolve) => {
          const existingScript =
            document.querySelector(
              `script[src="${YOUTUBE_API_SRC}"]`
            );

          if (
            !existingScript
          ) {
            const script =
              document.createElement(
                "script"
              );

            script.src =
              YOUTUBE_API_SRC;

            document.head.appendChild(
              script
            );
          }

          const interval =
            window.setInterval(
              () => {
                if (
                  window.YT &&
                  window.YT.Player
                ) {
                  window.clearInterval(
                    interval
                  );

                  resolve();
                }
              },
              100
            );
        }
      );

    return youtubeApiPromise;
  };


  /* ==================================================
     MEDIA
  ================================================== */

  const getMediaItems = (
    material
  ) => {
    const mediaItems =
      Array.isArray(
        material.media
      )
        ? [
            ...material.media
          ]
        : [];

    /*
      Video primero.
      Después respetamos sortOrder.
    */
    mediaItems.sort(
      (a, b) => {
        const aIsVideo =
          a?.mediaType ===
          "youtube";

        const bIsVideo =
          b?.mediaType ===
          "youtube";

        if (
          aIsVideo &&
          !bIsVideo
        ) {
          return -1;
        }

        if (
          !aIsVideo &&
          bIsVideo
        ) {
          return 1;
        }

        return (
          Number(
            a?.sortOrder ??
              0
          ) -
          Number(
            b?.sortOrder ??
              0
          )
        );
      }
    );

    const validMedia =
      mediaItems.filter(
        (media) => {
          return (
            media?.url &&
            (
              media.mediaType ===
                "image" ||
              media.mediaType ===
                "youtube"
            )
          );
        }
      );

    /*
      Fallback si no existe media.
    */
    if (
      !validMedia.length &&
      material.imageUrl
    ) {
      validMedia.push({
        id:
          "fallback-image",

        mediaType:
          "image",

        url:
          material.imageUrl,

        sortOrder:
          -1
      });
    }

    return validMedia;
  };


  /* ==================================================
     VIDEO SLIDE
  ================================================== */

  const createVideoSlide = (
    media,
    material
  ) => {
    const videoId =
      getYouTubeVideoId(
        media.url
      );

    /*
      Usamos primero una portada propia
      si la API la llegara a proporcionar.

      Si no, usamos la miniatura
      automática de YouTube.
    */
    const thumbnailUrl =
      media.thumbnailUrl ||
      media.imageUrl ||
      getYouTubeThumbnail(
        media.url
      );

    const videoSlide =
      document.createElement(
        "div"
      );

    videoSlide.className =
      "peguu-material-detail__video-slide";

    let player = null;


    /* ----------------------------------------------
       RESTAURAR PORTADA
    ---------------------------------------------- */

    const renderPreview =
      () => {
        if (
          player &&
          typeof player.destroy ===
            "function"
        ) {
          try {
            player.destroy();
          } catch {
            /*
              El reproductor puede
              haberse eliminado antes.
            */
          }
        }

        player = null;

        videoSlide.replaceChildren();


        /* PORTADA */

        if (thumbnailUrl) {
          const preview =
            document.createElement(
              "img"
            );

          preview.src =
            thumbnailUrl;

          preview.alt =
            `Video de ${
              material.name ||
              "material"
            }`;

          preview.draggable =
            false;

          videoSlide.appendChild(
            preview
          );
        }


        /* PLAY PRINCIPAL */

        const playButton =
          document.createElement(
            "button"
          );

        playButton.className =
          "peguu-material-detail__video-button";

        playButton.type =
          "button";

        playButton.setAttribute(
          "aria-label",
          `Reproducir video de ${
            material.name ||
            "material"
          }`
        );

        playButton.innerHTML =
          getMainPlaySvg();


        /* REPRODUCIR */

        playButton.addEventListener(
          "click",
          async () => {
            if (!videoId) {
              return;
            }

            await loadYouTubeApi();

            const playerContainer =
              document.createElement(
                "div"
              );

            playerContainer.id =
              `peguu-youtube-${videoId}-${Date.now()}`;

            videoSlide.replaceChildren(
              playerContainer
            );

            player =
              new window.YT.Player(
                playerContainer.id,
                {
                  videoId,

                  width:
                    "100%",

                  height:
                    "100%",

                  playerVars: {
                    autoplay: 1,
                    rel: 0,
                    playsinline: 1
                  },

                  events: {
                    onStateChange:
                      (
                        event
                      ) => {
                        if (
                          event.data ===
                          window.YT
                            .PlayerState
                            .ENDED
                        ) {
                          renderPreview();
                        }
                      }
                  }
                }
              );
          }
        );

        videoSlide.appendChild(
          playButton
        );
      };


    /*
      Permite detener y restaurar
      cuando se cambia de slide.
    */
    videoSlide.restorePreview =
      renderPreview;

    renderPreview();

    return videoSlide;
  };


  /* ==================================================
     CARRUSEL
  ================================================== */

  const renderCarousel = (
    section,
    material
  ) => {
    const carousel =
      section.querySelector(
        "[data-material-carousel]"
      );

    const track =
      section.querySelector(
        "[data-material-carousel-track]"
      );

    const thumbnails =
      section.querySelector(
        "[data-material-thumbnails]"
      );

    const dotsContainer =
      section.querySelector(
        "[data-material-carousel-dots]"
      );

    const counter =
      section.querySelector(
        "[data-material-carousel-counter]"
      );

    const previousButton =
      section.querySelector(
        "[data-material-carousel-prev]"
      );

    const nextButton =
      section.querySelector(
        "[data-material-carousel-next]"
      );

    if (
      !carousel ||
      !track ||
      !thumbnails ||
      !dotsContainer
    ) {
      return;
    }

    track.replaceChildren();
    thumbnails.replaceChildren();
    dotsContainer.replaceChildren();

    const mediaItems =
      getMediaItems(material);

    if (!mediaItems.length) {
      return;
    }

    const slides = [];
    const thumbnailButtons = [];
    const dotButtons = [];

    let activeIndex = 0;
    let scrollTimer = null;
    let autoplayTimer = null;
    let resumeTimer = null;
    let isDragging = false;
    let isPausedByUser = false;
    let isLoopJumping = false;
    let startX = 0;
    let startScrollLeft = 0;

    const autoplayDelay = 5500;
    const autoplayResumeDelay = 8000;

    mediaItems.forEach(
      (media, index) => {
        const slide =
          document.createElement(
            "div"
          );

        slide.className =
          "peguu-material-detail__slide";

        slide.dataset.slideIndex =
          String(index);

        slide.setAttribute(
          "role",
          "group"
        );

        slide.setAttribute(
          "aria-roledescription",
          "diapositiva"
        );

        slide.setAttribute(
          "aria-label",
          `${index + 1} de ${mediaItems.length}`
        );

        if (
          media.mediaType ===
          "youtube"
        ) {
          slide.appendChild(
            createVideoSlide(
              media,
              material
            )
          );
        } else {
          const image =
            document.createElement(
              "img"
            );

          image.className =
            "peguu-material-detail__slide-image";

          image.src = media.url;
          image.alt =
            material.name ||
            "Material Peguu";

          image.loading =
            index === 0
              ? "eager"
              : "lazy";

          image.decoding = "async";
          image.draggable = false;

          slide.appendChild(image);
        }

        track.appendChild(slide);
        slides.push(slide);

        const thumbnailButton =
          document.createElement(
            "button"
          );

        thumbnailButton.className =
          "peguu-material-detail__thumbnail";

        thumbnailButton.type =
          "button";

        if (index === 0) {
          thumbnailButton.classList.add(
            "is-active"
          );
        }

        if (
          media.mediaType ===
          "youtube"
        ) {
          const thumbnailUrl =
            media.thumbnailUrl ||
            media.imageUrl ||
            getYouTubeThumbnail(
              media.url
            );

          thumbnailButton.setAttribute(
            "aria-label",
            `Ver video de ${
              material.name ||
              "material"
            }`
          );

          thumbnailButton.innerHTML = `
            <span
              class="peguu-material-detail__thumbnail-video"
              aria-hidden="true"
            >
              ${
                thumbnailUrl
                  ? `
                    <img
                      src="${escapeHtml(
                        thumbnailUrl
                      )}"
                      alt=""
                      width="85"
                      height="71"
                    >
                  `
                  : ""
              }

              <span
                class="peguu-material-detail__thumbnail-play"
              >
                ${getThumbnailPlaySvg(index)}
              </span>
            </span>
          `;
        } else {
          thumbnailButton.setAttribute(
            "aria-label",
            `Ver imagen de ${
              material.name ||
              "material"
            }`
          );

          const thumbnailImage =
            document.createElement(
              "img"
            );

          thumbnailImage.src =
            media.url;

          thumbnailImage.alt = "";
          thumbnailImage.width = 85;
          thumbnailImage.height = 71;
          thumbnailImage.draggable = false;

          thumbnailButton.appendChild(
            thumbnailImage
          );
        }

        thumbnails.appendChild(
          thumbnailButton
        );

        thumbnailButtons.push(
          thumbnailButton
        );

        const dotButton =
          document.createElement(
            "button"
          );

        dotButton.className =
          "peguu-material-detail__carousel-dot";

        dotButton.type =
          "button";

        dotButton.setAttribute(
          "aria-label",
          `Ver elemento ${index + 1} de ${mediaItems.length}`
        );

        if (index === 0) {
          dotButton.classList.add(
            "is-active"
          );

          dotButton.setAttribute(
            "aria-current",
            "true"
          );
        }

        dotsContainer.appendChild(
          dotButton
        );

        dotButtons.push(
          dotButton
        );
      }
    );

    const hasMultipleSlides =
      slides.length > 1;

    const hasMobileLoop =
      hasMultipleSlides &&
      window.matchMedia(
        "(max-width: 600px)"
      ).matches;

    let leadingClone = null;
    let trailingClone = null;

    if (hasMobileLoop) {
      leadingClone =
        slides[slides.length - 1]
          .cloneNode(true);

      trailingClone =
        slides[0].cloneNode(true);

      [leadingClone, trailingClone]
        .forEach((clone) => {
          clone.classList.add(
            "peguu-material-detail__slide--clone"
          );
          clone.setAttribute(
            "aria-hidden",
            "true"
          );
          clone.setAttribute(
            "inert",
            ""
          );
        });

      track.prepend(leadingClone);
      track.append(trailingClone);
    }

    if (counter) {
      counter.hidden =
        !hasMultipleSlides;
    }

    if (previousButton) {
      previousButton.hidden =
        !hasMultipleSlides;
    }

    if (nextButton) {
      nextButton.hidden =
        !hasMultipleSlides;
    }

    const getElementLeft =
      (element) => {
        const trackPaddingLeft =
          Number.parseFloat(
            window.getComputedStyle(track)
              .paddingLeft
          ) || 0;

        return Math.max(
          0,
          (element?.offsetLeft ?? 0) -
            trackPaddingLeft
        );
      };

    const getSlideLeft =
      (index) => {
        return getElementLeft(
          slides[index]
        );
      };

    const jumpToSlide =
      (index) => {
        isLoopJumping = true;

        track.classList.add(
          "is-loop-jumping"
        );

        track.scrollLeft =
          getSlideLeft(index);

        window.requestAnimationFrame(
          () => {
            window.requestAnimationFrame(
              () => {
                track.classList.remove(
                  "is-loop-jumping"
                );

                isLoopJumping = false;
              }
            );
          }
        );
      };

    const getLoopTarget =
      (index) => {
        if (
          hasMobileLoop &&
          index < 0
        ) {
          return {
            element: leadingClone,
            index: slides.length - 1
          };
        }

        if (
          hasMobileLoop &&
          index >= slides.length
        ) {
          return {
            element: trailingClone,
            index: 0
          };
        }

        const normalizedIndex =
          (
            index + slides.length
          ) % slides.length;

        return {
          element: slides[normalizedIndex],
          index: normalizedIndex
        };
      };

    const getClosestSlideIndex =
      () => {
        let closestIndex = 0;
        let closestDistance =
          Number.POSITIVE_INFINITY;

        slides.forEach(
          (slide, index) => {
            const distance =
              Math.abs(
                track.scrollLeft -
                getElementLeft(slide)
              );

            if (
              distance <
              closestDistance
            ) {
              closestDistance =
                distance;

              closestIndex = index;
            }
          }
        );

        return closestIndex;
      };

    const clearAutoplay = () => {
      window.clearInterval(
        autoplayTimer
      );

      autoplayTimer = null;
    };

    const currentSlideHasPlayingVideo =
      () => {
        return Boolean(
          slides[activeIndex]
            ?.querySelector("iframe")
        );
      };

    const startAutoplay = () => {
      clearAutoplay();

      if (
        !hasMultipleSlides ||
        document.hidden ||
        isPausedByUser ||
        window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches
      ) {
        return;
      }

      autoplayTimer =
        window.setInterval(
          () => {
            if (
              document.hidden ||
              isDragging ||
              currentSlideHasPlayingVideo()
            ) {
              return;
            }

            goToSlide(
              activeIndex + 1,
              false
            );
          },
          autoplayDelay
        );
    };

    const pauseTemporarily = () => {
      isPausedByUser = true;
      clearAutoplay();

      window.clearTimeout(
        resumeTimer
      );

      resumeTimer =
        window.setTimeout(
          () => {
            isPausedByUser = false;
            startAutoplay();
          },
          autoplayResumeDelay
        );
    };

    const setActiveIndex =
      (nextIndex) => {
        if (
          nextIndex < 0 ||
          nextIndex >= slides.length
        ) {
          return;
        }

        if (
          activeIndex !== nextIndex
        ) {
          const previousVideo =
            slides[activeIndex]
              ?.querySelector(
                ".peguu-material-detail__video-slide"
              );

          if (
            previousVideo &&
            typeof previousVideo
              .restorePreview ===
              "function"
          ) {
            previousVideo.restorePreview();
          }
        }

        activeIndex = nextIndex;

        slides.forEach(
          (slide, index) => {
            const isActive =
              index === activeIndex;

            slide.classList.toggle(
              "is-active",
              isActive
            );

            slide.setAttribute(
              "aria-hidden",
              isActive
                ? "false"
                : "true"
            );
          }
        );

        thumbnailButtons.forEach(
          (button, index) => {
            const isActive =
              index === activeIndex;

            button.classList.toggle(
              "is-active",
              isActive
            );

            button.setAttribute(
              "aria-current",
              isActive
                ? "true"
                : "false"
            );
          }
        );

        dotButtons.forEach(
          (button, index) => {
            const isActive =
              index === activeIndex;

            button.classList.toggle(
              "is-active",
              isActive
            );

            if (isActive) {
              button.setAttribute(
                "aria-current",
                "true"
              );
            } else {
              button.removeAttribute(
                "aria-current"
              );
            }
          }
        );

        if (counter) {
          counter.textContent =
            `${activeIndex + 1} / ${slides.length}`;
        }

        const activeThumbnail =
          thumbnailButtons[activeIndex];

        if (activeThumbnail) {
          const targetLeft =
            activeThumbnail.offsetLeft -
            (
              thumbnails.clientWidth -
              activeThumbnail.offsetWidth
            ) /
              2;

          thumbnails.scrollTo({
            left:
              Math.max(
                0,
                targetLeft
              ),
            behavior: "smooth"
          });
        }
      };

    const goToSlide = (
      index,
      userInitiated = false
    ) => {
      if (!slides.length) {
        return;
      }

      const target =
        getLoopTarget(index);

      if (userInitiated) {
        pauseTemporarily();
      }

      track.scrollTo({
        left:
          getElementLeft(
            target.element
          ),
        behavior: "smooth"
      });

      setActiveIndex(
        target.index
      );
    };

    thumbnailButtons.forEach(
      (button, index) => {
        button.addEventListener(
          "click",
          () => {
            goToSlide(
              index,
              true
            );
          }
        );
      }
    );

    dotButtons.forEach(
      (button, index) => {
        button.addEventListener(
          "click",
          () => {
            goToSlide(
              index,
              true
            );
          }
        );
      }
    );

    previousButton?.addEventListener(
      "click",
      () => {
        goToSlide(
          activeIndex - 1,
          true
        );
      }
    );

    nextButton?.addEventListener(
      "click",
      () => {
        goToSlide(
          activeIndex + 1,
          true
        );
      }
    );

    track.addEventListener(
      "scroll",
      () => {
        if (isLoopJumping) {
          return;
        }

        window.clearTimeout(
          scrollTimer
        );

        scrollTimer =
          window.setTimeout(
            () => {
              if (hasMobileLoop) {
                const physicalSlides =
                  [
                    leadingClone,
                    ...slides,
                    trailingClone
                  ];

                const closestSlide =
                  physicalSlides.reduce(
                    (closest, slide) => {
                      if (!slide) {
                        return closest;
                      }

                      const distance =
                        Math.abs(
                          track.scrollLeft -
                          getElementLeft(slide)
                        );

                      return (
                        !closest ||
                        distance <
                          closest.distance
                      )
                        ? { slide, distance }
                        : closest;
                    },
                    null
                  )?.slide;

                if (closestSlide === leadingClone) {
                  setActiveIndex(
                    slides.length - 1
                  );
                  jumpToSlide(
                    slides.length - 1
                  );
                  return;
                }

                if (closestSlide === trailingClone) {
                  setActiveIndex(0);
                  jumpToSlide(0);
                  return;
                }
              }

              setActiveIndex(
                getClosestSlideIndex()
              );
            },
            80
          );
      },
      {
        passive: true
      }
    );

    track.addEventListener(
      "pointerdown",
      (event) => {
        if (
          event.target.closest(
            "button, iframe"
          )
        ) {
          return;
        }

        isDragging = true;
        startX = event.clientX;
        startScrollLeft =
          track.scrollLeft;

        track.classList.add(
          "is-dragging"
        );

        pauseTemporarily();

        track.setPointerCapture?.(
          event.pointerId
        );
      }
    );

    track.addEventListener(
      "pointermove",
      (event) => {
        if (!isDragging) {
          return;
        }

        track.scrollLeft =
          startScrollLeft -
          (
            event.clientX -
            startX
          );
      }
    );

    const stopDragging =
      (event) => {
        if (!isDragging) {
          return;
        }

        isDragging = false;

        track.classList.remove(
          "is-dragging"
        );

        track.releasePointerCapture?.(
          event.pointerId
        );

        goToSlide(
          getClosestSlideIndex(),
          true
        );
      };

    track.addEventListener(
      "pointerup",
      stopDragging
    );

    track.addEventListener(
      "pointercancel",
      stopDragging
    );

    track.addEventListener(
      "pointerleave",
      (event) => {
        if (
          event.pointerType ===
          "mouse"
        ) {
          stopDragging(event);
        }
      }
    );

    carousel.addEventListener(
      "mouseenter",
      clearAutoplay
    );

    carousel.addEventListener(
      "mouseleave",
      startAutoplay
    );

    carousel.addEventListener(
      "focusin",
      clearAutoplay
    );

    carousel.addEventListener(
      "focusout",
      startAutoplay
    );

    carousel.addEventListener(
      "touchstart",
      pauseTemporarily,
      {
        passive: true
      }
    );

    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) {
          clearAutoplay();
        } else {
          startAutoplay();
        }
      }
    );

    setActiveIndex(0);

    if (hasMobileLoop) {
      window.requestAnimationFrame(
        () => {
          jumpToSlide(0);
        }
      );
    }

    startAutoplay();
  };

  /* ==================================================
     ESPECIFICACIONES
  ================================================== */

  const getSpecifications = (
    material
  ) => {
    return [
      {
        label:
          "Tipo de vinil",

        value:
          material.vinylType
      },

      {
        label:
          "Durabilidad",

        value:
          material.durability
      },

      {
        label:
          "Resistencia al agua",

        value:
          material
            .waterResistant ===
          true
            ? "Sí"
            : material
                  .waterResistant ===
                false
              ? "No"
              : ""
      },

      {
        label:
          "Colores",

        value:
          material.colors
      },

      {
        label:
          "Tecnología",

        value:
          material.technology
      },

      {
        label:
          "Uso recomendado",

        value:
          material.recommendedUse
      }
    ].filter(
      (item) => {
        return (
          item.value !==
            null &&
          item.value !==
            undefined &&
          String(
            item.value
          ).trim() !==
            ""
        );
      }
    );
  };


  const renderSpecifications = (
    container,
    specifications
  ) => {
    container.replaceChildren();

    specifications.forEach(
      (
        specification
      ) => {
        const row =
          document.createElement(
            "div"
          );

        row.className =
          "peguu-material-detail__specification-row";

        row.innerHTML = `
          <dt>
            ${escapeHtml(
              specification.label
            )}
          </dt>

          <dd>
            ${escapeHtml(
              specification.value
            )}
          </dd>
        `;

        container.appendChild(
          row
        );
      }
    );
  };



  /* ==================================================
     ETIQUETAS DEL MATERIAL
  ================================================== */

  const renderMaterialBadges = (
    section,
    material
  ) => {
    const container =
      section.querySelector(
        "[data-material-badges]"
      );

    const topSellerBadge =
      section.querySelector(
        "[data-material-top-seller]"
      );

    const offerBadge =
      section.querySelector(
        "[data-material-offer]"
      );

    if (
      !container ||
      !topSellerBadge ||
      !offerBadge
    ) {
      return;
    }

    const isTopSeller =
      material?.isTopSeller === true;

    const discountPercent =
      Number(
        material?.discountPercent
      ) || 0;

    const isOnOffer =
      discountPercent > 0;

    topSellerBadge.hidden =
      !isTopSeller;

    offerBadge.hidden =
      !isOnOffer;

    if (isOnOffer) {
      offerBadge.textContent =
        "En oferta";

      offerBadge.setAttribute(
        "aria-label",
        `En oferta, ${discountPercent}% de descuento`
      );

      offerBadge.title =
        `${discountPercent}% de descuento`;
    } else {
      offerBadge.removeAttribute(
        "aria-label"
      );

      offerBadge.removeAttribute(
        "title"
      );
    }

    container.hidden =
      !isTopSeller &&
      !isOnOffer;
  };


  /* ==================================================
     MATERIAL
  ================================================== */

  const renderMaterial = (
    section,
    material
  ) => {
    const status =
      section.querySelector(
        "[data-material-detail-status]"
      );

    const content =
      section.querySelector(
        "[data-material-detail-content]"
      );

    const title =
      section.querySelector(
        "[data-material-title]"
      );

    const description =
      section.querySelector(
        "[data-material-description]"
      );

    const descriptionContainer =
      description?.closest(
        ".peguu-material-detail__description"
      );

    const highlight =
      section.querySelector(
        "[data-material-highlight]"
      );

    const specifications =
      section.querySelector(
        "[data-material-specifications]"
      );

    const selectButton =
      section.querySelector(
        "[data-select-material]"
      );


    title.textContent =
      material.name ||
      "Material";

    const descriptionText =
      String(
        material.description ||
        ""
      ).trim();

    description.textContent =
      descriptionText;

    descriptionContainer?.classList.remove(
      "peguu-material-detail__description--long",
      "peguu-material-detail__description--very-long"
    );

    if (
      descriptionText.length > 420
    ) {
      descriptionContainer?.classList.add(
        "peguu-material-detail__description--very-long"
      );
    } else if (
      descriptionText.length > 180
    ) {
      descriptionContainer?.classList.add(
        "peguu-material-detail__description--long"
      );
    }

    highlight.textContent =
      "";

    highlight.hidden =
      true;


    renderMaterialBadges(
      section,
      material
    );


    renderCarousel(
      section,
      material
    );


    renderSpecifications(
      specifications,
      getSpecifications(
        material
      )
    );


    selectButton?.addEventListener(
      "click",
      () => {

        /* =========================
          MATERIAL SELECCIONADO
        ========================= */

        const selectedMaterial = {
          id:
            material.id,

          name:
            material.name || "",

          slug:
            createSlug(
              material.name
            ),

          productCode:
            material.productCode || "",

          imageUrl:
            material.imageUrl || "",

          discountPercent:
            material.discountPercent ?? 0,

          isTopSeller:
            material.isTopSeller === true
        };

        const previousMaterial =
          getStoredMaterial();

        if (
          previousMaterial?.id &&
          String(previousMaterial.id) !==
            String(selectedMaterial.id)
        ) {
          clearPreviousPurchaseData();
        }


        /* =========================
          GUARDAR MATERIAL
        ========================= */

        saveStoredMaterial(
          selectedMaterial
        );


        /* =========================
          NORMALIZAR PRODUCT CODE
        ========================= */

        const productCode =
          String(
            selectedMaterial.productCode ||
            ""
          )
            .trim()
            .toLowerCase();


        /* =========================
          RUTAS
        ========================= */

        const builderUrl =
          section.dataset.builderUrl ||
          "/pages/arma-tus-stickers";


        const qrConfigUrl =
          section.dataset.qrConfigUrl ||
          "/pages/configura-qr";


        /* =========================
          DECIDIR FLUJO
        ========================= */

        const destinationUrl =
          productCode === "qr_sticker"
            ? qrConfigUrl
            : builderUrl;


        /* =========================
          REDIRECCIÓN
        ========================= */

        window.location.assign(
          destinationUrl
        );
      }
    );


    status.hidden =
      true;

    content.hidden =
      false;
  };


  const renderResolvedMaterial = async (
    section,
    detailApiBaseUrl,
    summaryMaterial
  ) => {
    const detailMaterial =
      await fetchMaterialDetail(
        detailApiBaseUrl,
        summaryMaterial.id
      );

    const material = {
      ...summaryMaterial,
      ...detailMaterial,

      id:
        detailMaterial.id ||
        summaryMaterial.id,

      name:
        detailMaterial.name ||
        summaryMaterial.name ||
        "",

      productCode:
        detailMaterial.productCode ||
        summaryMaterial.productCode ||
        "",

      imageUrl:
        detailMaterial.imageUrl ||
        summaryMaterial.imageUrl ||
        "",

      discountPercent:
        summaryMaterial.discountPercent ??
        detailMaterial.discountPercent ??
        0,

      isTopSeller:
        summaryMaterial.isTopSeller === true ||
      detailMaterial.isTopSeller === true
    };

    if (isSamplePack(material)) {
      redirectSamplePack();
      return;
    }

    const previousMaterial =
      getStoredMaterial();

    if (
      previousMaterial?.id &&
      String(previousMaterial.id) !==
        String(material.id)
    ) {
      clearPreviousPurchaseData();
    }

    saveStoredMaterial({
      id: material.id,
      name: material.name,
      slug: createSlug(material.name),
      productCode: material.productCode,
      imageUrl: material.imageUrl,
      discountPercent: material.discountPercent,
      isTopSeller: material.isTopSeller
    });

    renderMaterial(section, material);
  };


  /* ==================================================
     ERROR
  ================================================== */

  const showError = (
    section,
    message,
    onRetry = null
  ) => {
    const status =
      section.querySelector(
        "[data-material-detail-status]"
      );

    const content =
      section.querySelector(
        "[data-material-detail-content]"
      );


    if (typeof onRetry === "function") {
      status.innerHTML = `
        <p>${message}</p>
        <button
          type="button"
          class="peguu-material-detail__retry"
          data-material-detail-retry
        >
          Reintentar
        </button>
      `;

      const retryButton = status.querySelector(
        "[data-material-detail-retry]"
      );

      retryButton?.addEventListener("click", async () => {
        retryButton.disabled = true;
        status.textContent = "Cargando material...";

        try {
          await onRetry();
        } catch (error) {
          console.error(
            "No fue posible cargar el detalle:",
            error
          );

          showError(
            section,
            "No pudimos cargar la información del material.",
            onRetry
          );
        }
      });
    } else {
      status.textContent = message;
    }

    status.hidden =
      false;

    content.hidden =
      true;
  };


  /* ==================================================
     INICIALIZACIÓN
  ================================================== */

  const initializeSection =
    async (
      section
    ) => {
      if (
        section.dataset
          .initialized ===
        "true"
      ) {
        return;
      }

      section.dataset.initialized =
        "true";

      const summaryApiUrl =
        window.PeguuApi.resolve(
          section.dataset
            .summaryApiUrl
        );

      const detailApiBaseUrl =
        window.PeguuApi.resolve(
          section.dataset
            .detailApiBaseUrl
        );

      const requestedSlug =
        getRequestedSlug();

      if (!requestedSlug) {
        showError(
          section,
          "No se indicó qué material deseas consultar."
        );

        return;
      }

      let summaryMaterial = null;

      try {
        /*
          El resumen siempre se consulta porque
          contiene isTopSeller y discountPercent.
        */

        const materials =
          await window.PeguuMaterials.getMaterials({
            url: summaryApiUrl
          });

        summaryMaterial =
          findMaterial(
            materials,
            requestedSlug
          );

        if (!summaryMaterial?.id) {
          showError(
            section,
            "No encontramos el material solicitado."
          );

          return;
        }

        if (isSamplePack(summaryMaterial)) {
          redirectSamplePack();
          return;
        }

        await renderResolvedMaterial(
          section,
          detailApiBaseUrl,
          summaryMaterial
        );
      } catch (error) {
        console.error(
          "No fue posible cargar el detalle:",
          error
        );

        showError(
          section,
          "No pudimos cargar la información del material.",
          summaryMaterial?.id
            ? () =>
                renderResolvedMaterial(
                  section,
                  detailApiBaseUrl,
                  summaryMaterial
                )
            : null
        );
      }
    };


  const initializeAll = (
    container = document
  ) => {
    const sections = container.matches?.(SECTION_SELECTOR)
      ? [container]
      : container.querySelectorAll(SECTION_SELECTOR);

    sections.forEach(initializeSection);
  };


  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeAll();
      }
    );
  } else {
    initializeAll();
  }


  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeAll(
        event.target
      );
    }
  );
})();
